import React from 'react';

import { Outlet } from 'react-router';
import { Navigate, Route, Routes } from 'react-router-dom';

import { RoutesList } from '@constants/routesList';
import { CreatePassword } from '@organisms/auth/passwordRecovery/createPassword/CreatePassword';
import { EmailVerification } from '@organisms/auth/passwordRecovery/emailVerification/EmailVerification';
import { SignIn } from '@organisms/auth/signIn/SignIn';
import { AuthPage } from '@pages/auth/Auth.page';
import { HelpCenterPage } from '@pages/helpCenter/HelpCenter.page';
import { HomePage } from '@pages/home/Home.page';
import { Main } from '@pages/main/Main';
import { LocateClosestWarehousePage } from '@pages/manageInventory/locateClosestWarehouse/LocateClosestWarehouse.page';
import { LocateProductPage } from '@pages/manageInventory/locateProduct/LocateProduct.page';
import { NotAllowedPage } from '@pages/notAllowedPage/NotAllowed.page';
import { NotFoundPage } from '@pages/notFoundPage/NotFound.page';
import { OrdersListPage } from '@pages/trackOrders/ordersList/OrdersList.page';
import { UserSettingsPage } from '@pages/userSettings/UserSettings.page';
import { ProtectedRoute } from '@routes/ProtectedRoute';
import App from 'App';

import {
    CycleCountPerformancePage,
    DockToStockPage,
    GlobalCapabilitiesPage,
    OrderHistoryPage,
    OutboundDriveFillRatePage,
    DrivePerformancePage,
    TenderToCarrierPerformancePage,
    InvoiceBillingPage,
    SpendAnalysisReport,
    DutiesTaxesAnalysisReport,
    InvoiceReport,
    StorageBillingReport,
    InventoryOnHandPage,
    WarehouseInventoryOnHandPage,
    InventoryVelocityAnalysisPage,
    MinMaxReviewPage,
    WarehousesAndCarrierCutoffsPage,
    MaterialAnalysisPage,
    MissingProductsPage,
    TransactionHistoryPage,
    ProductMasterPage,
    SubstituteProductsPage,
    ProofOfDeliveryPage,
    OpenOrdersPage,
    OutstandingPartsOPDPage
} from './lazyDundasPages';
import {
    ChoiceAdminPage,
    AdminUsersPage,
    ChoiceAdminCreatePage,
    AdminsPage,
    AdminCreatePage,
    CustomerOrderPage,
    AdminUserCreatePage,
    DeliveryUploadPage,
    OrderDetailsPage,
    ProfilesCreatePage,
    ProfilesPage,
    MirDetailsPage,
    ReplenishmentUploadPage,
    GoodToChoicePage,
    TransfersUploadPage,
    MaterialInReviewPage,
    ReplenishmentOrderPage,
    TransferOrderPage,
    FromChoiceOrderPage
} from './lazyOrderPages';

export const AppRoutes = () => {
    const {
        AUTH,
        ROOT,
        ORDERS,
        TRANSFERS,
        NOT_FOUND,
        TRACK_ORDERS,
        REPLENISHMENTS,
        MANAGE_INVENTORY,
        NOT_ALLOWED,
        ADMIN_PANEL,
        BILLING,
        ANALYTICS,
        HELP_CENTER,
        SETTINGS,
        HOME,
        RETURNS
    } = RoutesList;

    return (
        <Routes>
            <Route element={<App />}>
                <Route path={AUTH.ROOT} element={<AuthPage />}>
                    <Route index element={<SignIn />} />
                    <Route
                        path={AUTH.VERIFICATION}
                        element={<EmailVerification />}
                    />
                    <Route
                        path={AUTH.CREATE_PASS}
                        element={<CreatePassword />}
                    />
                </Route>
                <Route path={ROOT} element={<Main />}>
                    {/* Home */}
                    <Route path={HOME.ROOT} element={<Outlet />}>
                        <Route
                            index
                            element={
                                <ProtectedRoute>
                                    <HomePage />
                                </ProtectedRoute>
                            }
                        />
                    </Route>

                    {/* TRACK ORDERS */}
                    <Route path={TRACK_ORDERS.ROOT} element={<Outlet />}>
                        <Route
                            index
                            element={
                                <ProtectedRoute>
                                    <OrdersListPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={TRACK_ORDERS.ORDER_DETAILS.VIEW}
                            element={
                                <ProtectedRoute>
                                    <OrderDetailsPage />
                                </ProtectedRoute>
                            }
                        />
                    </Route>

                    {/* ORDERS */}
                    <Route path={ORDERS.ROOT} element={<Outlet />}>
                        <Route index element={<Navigate to={NOT_FOUND} />} />
                        <Route
                            path={ORDERS.BY_PART.CREATE}
                            element={
                                <ProtectedRoute>
                                    <CustomerOrderPage isDefective={false} />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path={ORDERS.BULK.CREATE}
                            element={
                                <ProtectedRoute>
                                    <DeliveryUploadPage />
                                </ProtectedRoute>
                            }
                        />
                    </Route>

                    {/* REPLENISHMENT */}
                    <Route path={REPLENISHMENTS.ROOT} element={<Outlet />}>
                        <Route
                            path={REPLENISHMENTS.GOOD.CREATE}
                            element={
                                <ProtectedRoute>
                                    <ReplenishmentOrderPage
                                        isDefective={false}
                                    />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={REPLENISHMENTS.BULK.CREATE}
                            element={
                                <ProtectedRoute>
                                    <ReplenishmentUploadPage
                                        isDefective={false}
                                    />
                                </ProtectedRoute>
                            }
                        />
                    </Route>

                    {/* Transfers */}
                    <Route path={TRANSFERS.ROOT} element={<Outlet />}>
                        <Route
                            path={TRANSFERS.UPLOAD.CREATE}
                            element={
                                <ProtectedRoute>
                                    <TransfersUploadPage isDefective={false} />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={TRANSFERS.UPLOAD_DEFECTIVE.CREATE}
                            element={
                                <ProtectedRoute>
                                    <TransfersUploadPage isDefective={true} />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={TRANSFERS.GOOD.CREATE}
                            element={
                                <ProtectedRoute>
                                    <TransferOrderPage isDefective={false} />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={TRANSFERS.DEFECTIVE.CREATE}
                            element={
                                <ProtectedRoute>
                                    <TransferOrderPage isDefective={true} />
                                </ProtectedRoute>
                            }
                        />
                    </Route>

                    {/* RETURNS */}
                    <Route path={RETURNS.ROOT} element={<Outlet />}>
                        <Route
                            path={RETURNS.DEFECTIVE_TO_CHOICE.CREATE}
                            element={
                                <ProtectedRoute>
                                    <ReplenishmentOrderPage
                                        isDefective={true}
                                    />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path={RETURNS.DEFECTIVE_TO_CHOICE_UPLOAD.CREATE}
                            element={
                                <ProtectedRoute>
                                    <ReplenishmentUploadPage
                                        isDefective={true}
                                    />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path={RETURNS.GOOD_TO_CHOICE.CREATE}
                            element={
                                <ProtectedRoute>
                                    <GoodToChoicePage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path={RETURNS.DEFECTIVE_FROM_CHOICE.CREATE}
                            element={
                                <ProtectedRoute>
                                    <FromChoiceOrderPage isDefective={true} />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path={RETURNS.DEFECTIVE_FROM_CHOICE_UPLOAD.CREATE}
                            element={
                                <ProtectedRoute>
                                    <DeliveryUploadPage isDefective={true} />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path={RETURNS.GOOD_FROM_CHOICE.CREATE}
                            element={
                                <ProtectedRoute>
                                    <FromChoiceOrderPage isDefective={false} />
                                </ProtectedRoute>
                            }
                        />
                    </Route>

                    {/* MANAGE INVENTORY */}
                    <Route path={MANAGE_INVENTORY.ROOT} element={<Outlet />}>
                        <Route
                            path={MANAGE_INVENTORY.PART_MASTER.READ}
                            element={
                                <ProtectedRoute>
                                    <ProductMasterPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={MANAGE_INVENTORY.SUBSTITUTE_PARTS.READ}
                            element={
                                <ProtectedRoute>
                                    <SubstituteProductsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={MANAGE_INVENTORY.INVENTORY_ON_HAND.ROOT}
                            element={
                                <ProtectedRoute>
                                    <InventoryOnHandPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={
                                MANAGE_INVENTORY.WAREHOUSE_INVENTORY_ON_HAND
                                    .ROOT
                            }
                            element={
                                <ProtectedRoute>
                                    <WarehouseInventoryOnHandPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={
                                MANAGE_INVENTORY.INVENTORY_VELOCITY_ANALYSIS
                                    .ROOT
                            }
                            element={
                                <ProtectedRoute>
                                    <InventoryVelocityAnalysisPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={MANAGE_INVENTORY.MIN_MAX_REVIEW.ROOT}
                            element={
                                <ProtectedRoute>
                                    <MinMaxReviewPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={
                                MANAGE_INVENTORY.WAREHOUSES_AND_CARRIER_CUTOFFS
                                    .ROOT
                            }
                            element={
                                <ProtectedRoute>
                                    <WarehousesAndCarrierCutoffsPage />
                                </ProtectedRoute>
                            }
                        />
                        {/* Legacy URL redirect after report rename (US-26690) */}
                        <Route
                            path="ssl-listing-and-carrier-cutoffs"
                            element={
                                <Navigate
                                    to={`../${MANAGE_INVENTORY.WAREHOUSES_AND_CARRIER_CUTOFFS.ROOT}`}
                                    replace
                                />
                            }
                        />
                        <Route
                            path={
                                MANAGE_INVENTORY.LOCATE_CLOSEST_WAREHOUSE.ROOT
                            }
                            element={
                                <ProtectedRoute>
                                    <LocateClosestWarehousePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={MANAGE_INVENTORY.LOCATE_PRODUCT.ROOT}
                            element={
                                <ProtectedRoute>
                                    <LocateProductPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={MANAGE_INVENTORY.MISSING_PARTS.ROOT}
                            element={
                                <ProtectedRoute>
                                    <MissingProductsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={
                                MANAGE_INVENTORY.INVENTORY_TRANSACTION_HISTORY
                                    .ROOT
                            }
                            element={
                                <ProtectedRoute>
                                    <TransactionHistoryPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={MANAGE_INVENTORY.OUTSTANDING_PARTS_OPD.ROOT}
                            element={
                                <ProtectedRoute>
                                    <OutstandingPartsOPDPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={MANAGE_INVENTORY.INVENTORY_OPEN_ORDERS.ROOT}
                            element={
                                <ProtectedRoute>
                                    <OpenOrdersPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={
                                MANAGE_INVENTORY.MATERIALS_IN_REVIEW_ANALYSIS
                                    .ROOT
                            }
                            element={
                                <ProtectedRoute>
                                    <MaterialAnalysisPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={MANAGE_INVENTORY.MATERIALS_REVIEW.ROOT}
                            element={<Outlet />}
                        >
                            <Route
                                index
                                element={
                                    <ProtectedRoute>
                                        <MaterialInReviewPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={
                                    MANAGE_INVENTORY.MATERIALS_REVIEW
                                        .MIR_DETAILS.VIEW
                                }
                                element={
                                    <ProtectedRoute>
                                        <MirDetailsPage />
                                    </ProtectedRoute>
                                }
                            />
                        </Route>
                    </Route>

                    {/* ADMIN PANEL */}
                    <Route path={ADMIN_PANEL.ROOT} element={<Outlet />}>
                        <Route
                            path={ADMIN_PANEL.PROFILES.READ.ROOT}
                            element={<Outlet />}
                        >
                            <Route
                                index
                                element={
                                    <ProtectedRoute>
                                        <ProfilesPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ADMIN_PANEL.PROFILES.CREATE.ROOT}
                                element={
                                    <ProtectedRoute>
                                        <ProfilesCreatePage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ADMIN_PANEL.PROFILES.EDIT.ROOT}
                                element={
                                    <ProtectedRoute>
                                        <ProfilesCreatePage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ADMIN_PANEL.PROFILES.VIEW.ROOT}
                                element={
                                    <ProtectedRoute>
                                        <ProfilesCreatePage />
                                    </ProtectedRoute>
                                }
                            />
                        </Route>
                        <Route
                            path={ADMIN_PANEL.CHOICE_ADMIN.READ.ROOT}
                            element={<Outlet />}
                        >
                            <Route
                                index
                                element={
                                    <ProtectedRoute>
                                        <ChoiceAdminPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ADMIN_PANEL.CHOICE_ADMIN.CREATE.ROOT}
                                element={
                                    <ProtectedRoute>
                                        <ChoiceAdminCreatePage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ADMIN_PANEL.CHOICE_ADMIN.EDIT.ROOT}
                                element={
                                    <ProtectedRoute>
                                        <ChoiceAdminCreatePage />
                                    </ProtectedRoute>
                                }
                            />
                        </Route>
                        <Route
                            path={ADMIN_PANEL.ADMINS.READ.ROOT}
                            element={<Outlet />}
                        >
                            <Route
                                index
                                element={
                                    <ProtectedRoute>
                                        <AdminsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ADMIN_PANEL.ADMINS.CREATE.ROOT}
                                element={
                                    <ProtectedRoute>
                                        <AdminCreatePage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ADMIN_PANEL.ADMINS.EDIT.ROOT}
                                element={
                                    <ProtectedRoute>
                                        <AdminCreatePage />
                                    </ProtectedRoute>
                                }
                            />
                        </Route>
                        <Route
                            path={ADMIN_PANEL.USERS.READ.ROOT}
                            element={<Outlet />}
                        >
                            <Route
                                index
                                element={
                                    <ProtectedRoute>
                                        <AdminUsersPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ADMIN_PANEL.USERS.CREATE.ROOT}
                                element={
                                    <ProtectedRoute>
                                        <AdminUserCreatePage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path={ADMIN_PANEL.USERS.EDIT.ROOT}
                                element={
                                    <ProtectedRoute>
                                        <AdminUserCreatePage />
                                    </ProtectedRoute>
                                }
                            />
                        </Route>
                    </Route>

                    {/* Billing */}
                    <Route path={BILLING.ROOT}>
                        <Route
                            path={BILLING.INVOICES.VIEW}
                            element={
                                <ProtectedRoute>
                                    <InvoiceReport />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={BILLING.STORAGE_BILLING.VIEW}
                            element={
                                <ProtectedRoute>
                                    <StorageBillingReport />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path={BILLING.INVOICE_BILLING.VIEW}
                            element={
                                <ProtectedRoute>
                                    <InvoiceBillingPage />
                                </ProtectedRoute>
                            }
                        />
                    </Route>

                    {/* Analytics*/}
                    <Route path={ANALYTICS.ROOT}>
                        <Route
                            path={ANALYTICS.DUTIES_TAXES_ANALYSIS.VIEW}
                            element={
                                <ProtectedRoute>
                                    <DutiesTaxesAnalysisReport />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={ANALYTICS.CLIENT_SPEND.VIEW}
                            element={
                                <ProtectedRoute>
                                    <SpendAnalysisReport />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={ANALYTICS.ORDER_HISTORY.READ}
                            element={
                                <ProtectedRoute>
                                    <OrderHistoryPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={ANALYTICS.CYCLE_COUNT_PERFORMANCE.READ}
                            element={
                                <ProtectedRoute>
                                    <CycleCountPerformancePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={ANALYTICS.GLOBAL_CAPABILITIES.READ}
                            element={
                                <ProtectedRoute>
                                    <GlobalCapabilitiesPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={ANALYTICS.DOCK_TO_STOCK.READ}
                            element={
                                <ProtectedRoute>
                                    <DockToStockPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={ANALYTICS.OUTBOUND_DRIVE_FILL_RATE.READ}
                            element={
                                <ProtectedRoute>
                                    <OutboundDriveFillRatePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={ANALYTICS.OUTBOUND_DRIVE_PERFORMANCE.READ}
                            element={
                                <ProtectedRoute>
                                    <DrivePerformancePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={ANALYTICS.TENDER_TO_CARRIER_PERFORMANCE.READ}
                            element={
                                <ProtectedRoute>
                                    <TenderToCarrierPerformancePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={ANALYTICS.PROOF_OF_DELIVERY.READ}
                            element={
                                <ProtectedRoute>
                                    <ProofOfDeliveryPage />
                                </ProtectedRoute>
                            }
                        />
                    </Route>

                    <Route path={SETTINGS} element={<UserSettingsPage />} />
                    <Route
                        path={ROOT}
                        element={<Navigate to={RoutesList.HOME.ROOT} />}
                    />
                    <Route path={HELP_CENTER} element={<HelpCenterPage />} />
                    <Route path={NOT_ALLOWED} element={<NotAllowedPage />} />
                </Route>
                <Route path="*" element={<NotFoundPage />} />
                <Route path={NOT_FOUND} element={<NotFoundPage />} />
            </Route>
        </Routes>
    );
};
